import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff, Mail, Lock, Truck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Login() {
  const demoMode = process.env.REACT_APP_DEMO_MODE === "1";
  const { login, formatApiError } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("expired") === "1") {
      toast.info("Session expirée — veuillez vous reconnecter");
    }
    sessionStorage.removeItem("redirectAfterLogin");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenue, ${user.name}`);
      if (user.role === "admin") nav("/admin");
      else if (user.role === "transporter") nav("/transporter");
      else nav("/shipper");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10 bg-white">
      {/* Decorative subtle blobs — uniquement variations du vert primaire */}
      <div aria-hidden className="absolute top-10 -left-20 w-80 h-80 rounded-full bg-[#1F4032]/5 blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute bottom-10 -right-20 w-96 h-96 rounded-full bg-[#1F4032]/8 blur-3xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1F4032] to-[#0F2A20] flex items-center justify-center shadow-lg shadow-[#1F4032]/30">
              <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-2xl tracking-tighter text-[#15302A]">MML</span>
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-[#15302A] mb-2">
            Bon retour parmi nous
          </h1>
          <p className="text-[#1F4032]/65 text-sm">
            Connectez-vous pour reprendre vos missions
          </p>
        </div>

        {/* Card */}
        <div className="card-soft p-7 md:p-8">
          <form onSubmit={submit} data-testid="login-form" className="space-y-5">
            {demoMode && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950">
                <div className="font-bold mb-2">Comptes de démonstration</div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="underline" onClick={() => { setEmail("expediteur@demo.mml"); setPassword("demo"); }}>Expéditeur</button>
                  <button type="button" className="underline" onClick={() => { setEmail("transporteur@demo.mml"); setPassword("demo"); }}>Transporteur</button>
                  <button type="button" className="underline" onClick={() => { setEmail("admin@demo.mml"); setPassword("demo"); }}>Administration</button>
                </div>
              </div>
            )}
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-[#1F4032] mb-2 ml-4 tracking-wide uppercase">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F4032]/45" />
                <input
                  id="email" type="email" required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@email.com"
                  className="input-pill w-full pl-12 pr-5 text-base text-[#15302A]"
                  data-testid="login-email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="pw" className="block text-xs font-semibold text-[#1F4032] mb-2 ml-4 tracking-wide uppercase">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F4032]/45" />
                <input
                  id="pw" type={showPassword ? "text" : "password"} required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-pill w-full pl-12 pr-12 text-base text-[#15302A]"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1F4032]/55 hover:text-[#1F4032]"
                  data-testid="login-toggle-password"
                  aria-label={showPassword ? "Masquer" : "Afficher"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Forgot link */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-[#1F4032] hover:text-[#0F2A20] font-medium"
                data-testid="login-forgot-link"
              >
                Mot de passe oublié ?
              </Link>
            </div>

            {/* CTA — UNIQUEMENT vert primaire avec son dégradé naturel */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-full bg-gradient-to-br from-[#1F4032] to-[#0F2A20] hover:from-[#2A5443] hover:to-[#1F4032] active:from-[#0F2A20] active:to-[#0F2A20] text-white font-semibold text-base transition-all disabled:opacity-60 shadow-lg shadow-[#1F4032]/30 hover:shadow-xl hover:shadow-[#1F4032]/40 hover:-translate-y-0.5 active:translate-y-0"
              data-testid="login-submit"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </button>

            {/* Trust line */}
            <div className="flex items-center justify-center gap-2 pt-1 text-xs text-[#1F4032]/65">
              <ShieldCheck className="w-3.5 h-3.5 text-[#1F4032]" />
              Connexion sécurisée — données chiffrées
            </div>
          </form>
        </div>

        {/* Bottom register link */}
        <div className="text-center mt-6 text-sm text-[#1F4032]/70">
          {"Pas encore de compte ? "}
          <Link
            to="/register"
            className="text-[#1F4032] font-semibold hover:underline underline-offset-4"
            data-testid="login-register-link"
          >
            Créer un compte
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
